import type { DemandChecklistItemDto } from '@tempo-io/fim3-dto';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DetailsRailTabDefinition } from '@/features/intake/components/shared/details-rail-layout/index';

export const DEMAND_TAB = {
  completeDetails: 'complete-details',
  evidence: 'evidence',
  customerImpact: 'customer-impact',
  workItems: 'work-items',
  reviews: 'reviews',
  linkedIdeas: 'linked-ideas',
} as const;

export type DemandTabId = (typeof DEMAND_TAB)[keyof typeof DEMAND_TAB];

export const DEMAND_TAB_IDS = Object.values(DEMAND_TAB) as DemandTabId[];

export const DEFAULT_DEMAND_TAB: DemandTabId = DEMAND_TAB.completeDetails;

/** Where a comment notification lands. Comments live alongside the Conversation feed. */
export const COMMENTS_TAB: DemandTabId = DEMAND_TAB.reviews;

export const isDemandTabId = (value: unknown): value is DemandTabId =>
  typeof value === 'string' && DEMAND_TAB_IDS.includes(value as DemandTabId);

type DemandTabContent = Record<DemandTabId, ReactNode>;

/**
 * The checklist steps (NEX-16801) use the same vocabulary as the tab ids, so a step's completion is
 * a lookup rather than a mapping table. A tab with no matching step gets no tick at all: that is
 * `customer-impact`, which the backend deliberately omits because the tab has no data model yet.
 */
const isStepComplete = (
  checklist: DemandChecklistItemDto[] | undefined,
  tabId: DemandTabId,
): boolean | undefined => checklist?.find((item) => item.step === tabId)?.isComplete ?? undefined;

export const useDemandDetailTabs = (
  content: DemandTabContent,
  checklist?: DemandChecklistItemDto[],
) => {
  const { t } = useTranslation('translation');

  const tabs: DetailsRailTabDefinition[] = [
    {
      id: DEMAND_TAB.completeDetails,
      title: t(($) => $.demand.general.demands.panel.tabs.completeDetails.title),
      helperText: t(($) => $.demand.general.demands.panel.tabs.completeDetails.helper),
      testId: 'fim3-demand-tab-complete-details',
      isComplete: isStepComplete(checklist, DEMAND_TAB.completeDetails),
      content: content[DEMAND_TAB.completeDetails],
    },
    {
      id: DEMAND_TAB.evidence,
      title: t(($) => $.demand.general.demands.panel.tabs.evidence.title),
      helperText: t(($) => $.demand.general.demands.panel.tabs.evidence.helper),
      testId: 'fim3-demand-tab-attachments',
      isComplete: isStepComplete(checklist, DEMAND_TAB.evidence),
      content: content[DEMAND_TAB.evidence],
    },
    {
      id: DEMAND_TAB.customerImpact,
      title: t(($) => $.demand.general.demands.panel.tabs.customerImpact.title),
      helperText: t(($) => $.demand.general.demands.panel.tabs.customerImpact.helper),
      testId: 'fim3-demand-tab-customer-impact',
      isComplete: isStepComplete(checklist, DEMAND_TAB.customerImpact),
      content: content[DEMAND_TAB.customerImpact],
    },
    {
      id: DEMAND_TAB.workItems,
      title: t(($) => $.demand.general.demands.panel.tabs.workItems.title),
      helperText: t(($) => $.demand.general.demands.panel.tabs.workItems.helper),
      testId: 'fim3-demand-tab-linked-items',
      isComplete: isStepComplete(checklist, DEMAND_TAB.workItems),
      content: content[DEMAND_TAB.workItems],
    },
    {
      id: DEMAND_TAB.reviews,
      title: t(($) => $.demand.general.demands.panel.tabs.reviews.title),
      helperText: t(($) => $.demand.general.demands.panel.tabs.reviews.helper),
      testId: 'fim3-demand-tab-reviews',
      isComplete: isStepComplete(checklist, DEMAND_TAB.reviews),
      content: content[DEMAND_TAB.reviews],
    },
    {
      id: DEMAND_TAB.linkedIdeas,
      title: t(($) => $.demand.general.demands.panel.linkedIdeas),
      helperText: t(($) => $.demand.general.demands.panel.tabs.linkedIdeas.helper),
      testId: 'fim3-demand-tab-linked-ideas',
      // Shown as a checklist row because the backend counts it as one of the five steps: leaving it
      // untickable would make a fifth of the percentage unexplainable from the list.
      isComplete: isStepComplete(checklist, DEMAND_TAB.linkedIdeas),
      content: content[DEMAND_TAB.linkedIdeas],
    },
  ];

  return { tabs };
};
